/**
 * Client-Side Typed Tracking Helpers
 *
 * Provides type-safe, domain-specific tracking functions for client-side analytics.
 *
 * Usage:
 * ```
 * import { track } from '@/lib/analytics';
 * track.offeringWizardStarted();
 * ```
 *
 * For server-side tracking, import from '@/lib/analytics/server':
 * ```
 * import { trackServer } from '@/lib/analytics/server';
 * await trackServer.offeringCreated(userId, { ... });
 * ```
 *
 * The `await` is not optional — every helper returns a delivery promise, and
 * `architecture/no-floating-tracking-call` fails Lint on a dropped one.
 */

import { trackEvent } from "@/lib/analytics/posthog";
import type {
  WizardStepProperties,
  WizardCompletedProperties,
  MapInteractionProperties,
  ParcelSelectedProperties,
  OrderBookViewedProperties,
  HomepageHeroCtaClickedProperties,
  EventCategory,
} from "@/lib/analytics/events";

// =============================================================================
// Client-Side Tracking
// =============================================================================

/**
 * Client-side tracking helpers
 * Use these in React components and client-side code
 */
export const track = {
  // ---------------------------------------------------------------------------
  // Wizard Events
  // ---------------------------------------------------------------------------

  /**
   * Track when user starts the offering creation wizard
   */
  offeringWizardStarted: () => trackEvent("offering_wizard_started"),

  /**
   * Track wizard step completion
   */
  offeringWizardStep: (props: WizardStepProperties) =>
    trackEvent("offering_wizard_step_completed", props),

  /**
   * Track successful wizard completion (before API submission)
   */
  offeringWizardCompleted: (props: WizardCompletedProperties) =>
    trackEvent("offering_wizard_completed", props),

  /**
   * Track wizard abandonment (user navigates away)
   */
  offeringWizardAbandoned: (props: Partial<WizardStepProperties>) =>
    trackEvent("offering_wizard_abandoned", props),

  /**
   * Track when user starts the request/bid creation wizard
   */
  requestWizardStarted: () => trackEvent("request_wizard_started"),

  /**
   * Track request wizard step completion
   */
  requestWizardStep: (props: WizardStepProperties) =>
    trackEvent("request_wizard_step_completed", props),

  /**
   * Track successful request wizard completion
   */
  requestWizardCompleted: (props: WizardCompletedProperties) =>
    trackEvent("request_wizard_completed", props),

  /**
   * Track request wizard abandonment
   */
  requestWizardAbandoned: (props: Partial<WizardStepProperties>) =>
    trackEvent("request_wizard_abandoned", props),

  // ---------------------------------------------------------------------------
  // User Events
  // ---------------------------------------------------------------------------

  /**
   * Track successful login
   */
  userLoggedIn: () => trackEvent("user_logged_in"),

  /**
   * Track logout
   */
  userLoggedOut: () => trackEvent("user_logged_out"),

  /**
   * Track profile update
   */
  profileUpdated: (fields: string[]) => trackEvent("profile_updated", { updated_fields: fields }),

  // ---------------------------------------------------------------------------
  // Engagement Events
  // ---------------------------------------------------------------------------

  /**
   * Track map interactions
   */
  mapInteraction: (props: MapInteractionProperties) => trackEvent("map_interaction", props),

  /**
   * Track parcel selection on map
   */
  parcelSelected: (props: ParcelSelectedProperties) => trackEvent("parcel_selected", props),

  /**
   * Track contact modal opening
   */
  contactModalOpened: (props: { offering_id?: string; request_id?: string }) =>
    trackEvent("contact_modal_opened", props),

  /**
   * Track contact form submission
   */
  contactFormSubmitted: (props: {
    offering_id?: string;
    request_id?: string;
    message_length: number;
  }) => trackEvent("contact_form_submitted", props),

  /**
   * Track order book view
   */
  orderBookViewed: (props: OrderBookViewedProperties) => trackEvent("order_book_viewed", props),

  /**
   * Track match card view
   */
  matchViewed: (props: { match_id: string; offering_id: string }) =>
    trackEvent("match_viewed", props),

  /**
   * Track offering detail page view
   */
  offeringDetailViewed: (props: { offering_id: string; type: string; bundesland: string }) =>
    trackEvent("offering_detail_viewed", props),

  /**
   * Track request detail page view
   */
  requestDetailViewed: (props: { request_id: string; type: string }) =>
    trackEvent("request_detail_viewed", props),

  /**
   * Track search performed
   */
  searchPerformed: (props: {
    query?: string;
    filters?: Record<string, unknown>;
    result_count: number;
  }) => trackEvent("search_performed", props),

  /**
   * Track filter applied
   */
  filterApplied: (props: { filter_type: string; filter_value: unknown }) =>
    trackEvent("filter_applied", props),

  /**
   * Track a click on the "Eine Marke der LandVest GmbH" footer link.
   * `placement` tells the two footer layouts apart.
   */
  landvestFooterLinkClicked: (props: { placement: "mobile" | "desktop" }) =>
    trackEvent("landvest_footer_link_clicked", props),

  /**
   * Track a click on a Flächenanalyse call to action that hands off to
   * LandVest. Sent instantly: the link leaves the site in the same tab, so a
   * batched event could be lost with the page.
   */
  landvestCtaClicked: (props: { placement: "pacht_article" }) =>
    trackEvent("landvest_cta_clicked", props, { sendInstantly: true }),

  /**
   * Track a click on an action in a homepage hero card. Sent instantly: the
   * seller card's analysis link leaves the site in the same tab.
   */
  homepageHeroCtaClicked: (props: HomepageHeroCtaClickedProperties) =>
    trackEvent("homepage_hero_cta_clicked", props, { sendInstantly: true }),

  // ---------------------------------------------------------------------------
  // Generic Event Tracking (with category)
  // ---------------------------------------------------------------------------

  /**
   * Track any event with a category tag
   */
  withCategory: (event: string, category: EventCategory, properties?: Record<string, unknown>) =>
    trackEvent(event, {
      ...properties,
      event_category: category,
    }),
};
